/**
 * Mobile device detection and utilities
 */

/**
 * Detects if the current device is a mobile device
 * @returns {boolean} True if mobile device
 */
export const isMobileDevice = () => {
  // Check user agent for mobile patterns
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const userAgentMatch = mobileRegex.test(navigator.userAgent);
  
  // Check for touch capability and screen size
  const touchCapable = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const smallScreen = window.innerWidth <= 768;
  
  return userAgentMatch || (touchCapable && smallScreen);
};

/**
 * Detects if the device supports touch
 * @returns {boolean} True if touch device
 */
export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Detects if the current browser is Safari on iOS
 * @returns {boolean} True if iOS Safari
 */
export const isIOSSafari = () => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
};

/**
 * Detects if the current browser is Chrome on Android
 * @returns {boolean} True if Android Chrome
 */
export const isAndroidChrome = () => {
  const ua = navigator.userAgent;
  return /Android/.test(ua) && /Chrome/.test(ua) && !/Edge|OPR/.test(ua);
};

/**
 * Gets the current viewport height accounting for mobile browser UI
 * @returns {number} Viewport height in pixels
 */
export const getViewportHeight = () => {
  // Use visualViewport API if available (better for mobile)
  if (window.visualViewport) {
    return window.visualViewport.height;
  }
  
  // Fallback to window.innerHeight
  return window.innerHeight;
};

/**
 * Estimates if the mobile keyboard is currently visible
 * @returns {boolean} True if keyboard is likely visible
 */
export const isKeyboardVisible = () => {
  if (!isMobileDevice()) return false;
  
  const currentHeight = getViewportHeight();
  const screenHeight = window.screen.height;
  
  // If viewport is significantly smaller than screen, keyboard is likely visible
  const keyboardThreshold = screenHeight * 0.75;
  return currentHeight < keyboardThreshold;
};

/**
 * Triggers haptic feedback if supported
 * @param {number} duration - Vibration duration in milliseconds
 */
export const triggerHapticFeedback = (duration = 50) => {
  if (navigator.vibrate && isMobileDevice()) {
    try {
      navigator.vibrate(duration);
    } catch (error) {
      // Silently fail if vibration is not supported
      console.debug('Haptic feedback not supported:', error);
    }
  }
};

/**
 * Prevents double-tap zoom on mobile elements
 * @param {HTMLElement} element - Element to prevent double-tap zoom on
 */
export const preventDoubleTabZoom = (element) => {
  if (!element || !isMobileDevice()) return;
  
  // Use CSS touch-action as the primary method
  element.style.touchAction = 'manipulation';
  
  element.addEventListener('touchstart', (e) => {
    // Only prevent default for multi-touch gestures, not single taps
    if (e.touches.length > 1) {
      e.preventDefault();
    }
  }, { passive: false });
};

/**
 * Scrolls element into view with mobile keyboard compensation
 * @param {HTMLElement} element - Element to scroll into view
 * @param {number} delay - Delay before scrolling (default 300ms)
 * @param {ScrollIntoViewOptions} options - Scroll options
 */
export const scrollIntoViewWithKeyboard = (element, delay = 300, options = {}) => {
  if (!element) return;
  
  const defaultOptions = {
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest'
  };
  
  const scrollOptions = { ...defaultOptions, ...options };
  
  if (!isMobileDevice()) {
    element.scrollIntoView(scrollOptions);
    return;
  }
  
  // For mobile, wait for keyboard to appear then scroll
  setTimeout(() => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = getViewportHeight();
    const keyboardHeight = viewportHeight * 0.4; // Estimate keyboard height
    
    // Only scroll if element is hidden by keyboard
    if (rect.bottom > viewportHeight - keyboardHeight) {
      element.scrollIntoView(scrollOptions);
    }
  }, delay);
};